import React, { useContext, useMemo, createContext } from 'react';
import { ControlPreviewFactory } from './ControlPreviewFactory';
import PanelHeader from './PanelHeader';
import ContextMenu from '../Common/ContextMenu';

import { WizardContext } from './hooks/WizardContext';
import { useDesktopControls } from './hooks/uiInteractions/useDesktopControls';
import { usePanelInteractions } from './hooks/uiInteractions/usePanelInteractions';
import { useDesktopDropTarget } from './hooks/uiInteractions/useDesktopDropTarget';
import { getBackgroundStyles } from './utils/mobileUtils';
import * as S from './styles/DesktopViewPanel.styles';


export const ContextMenuContext = createContext({ showMenu: () => {}, hideMenu: () => {} });


const DesktopViewPanel = ({ onClose }) => {
    const { activePage, appAttributes, selectedControlId, removeControl, selectControl } = useContext(WizardContext);
    
    const { controls, isLoading, sendControlToBack } = useDesktopControls();
    const interactions = usePanelInteractions(selectedControlId, selectControl);
    const dnd = useDesktopDropTarget(removeControl, activePage?.id);

    const title = useMemo(() => {
        if (selectedControlId) {
            const control = controls.find(c => c.id === selectedControlId);
            return control ? `${activePage.name} - ${control.name}` : `${activePage.name} - Desktop View`;
        }
        return `${activePage.name} - Desktop View`;
    }, [activePage, selectedControlId, controls]);

    if (isLoading || !activePage || !appAttributes) {
        return <div>Loading Preview...</div>;
    }

    const pageAttributes = getBackgroundStyles(activePage);

    return (
        <ContextMenuContext.Provider value={{ showMenu: interactions.showMenu, hideMenu: interactions.hideMenu }}>
            <S.PanelWrapper
                onClick={interactions.handlePanelClick}
                ref={dnd.drop}
                bgColor={appAttributes.backgroundColor}
            >
                <PanelHeader title={title} showCloseButton={true} onClose={onClose} />
                <S.ContentArea>
                    <S.DesktopAppContainer
                        pageAttrs={pageAttributes}
                        frameStyles={appAttributes.frame}
                        isOver={dnd.isOver}
                        canDrop={dnd.canDrop}
                    >
                        {controls.map(controlData => (
                            <ControlPreviewFactory
                                key={controlData.id}
                                control={controlData}
                                renderMode="preview"
                                isSelected={controlData.id === selectedControlId}
                                onSendToBack={() => {
                                    sendControlToBack(controlData.id);
                                    interactions.hideMenu();
                                }}
                                onClick={interactions.handleControlClick}
                                setSelectedControlId={selectControl}
                                onHideTooltip={interactions.hideTooltip}
                            />
                        ))}
                    </S.DesktopAppContainer>
                </S.ContentArea>
                <ContextMenu menuState={interactions.menuState} />
                {interactions.tooltip.visible && (
                    <S.Tooltip x={interactions.tooltip.x} y={interactions.tooltip.y}>
                        {interactions.tooltip.content}
                    </S.Tooltip>
                )}
            </S.PanelWrapper>
        </ContextMenuContext.Provider>
    );
};

export default DesktopViewPanel;